import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  createStripeAccountId,
  getInvoiceDetails,
  getPaymentAccounts,
  getPaymentLinks,
  getPaymentsHistory,
  getPaymentsOverview,
  getTotalPaymentsPerDay,
  getMinimumServicePrices,
  getWalletSummary,
} from "../../services/payment";
import { TableActiveState } from "../../../pages/Payment/payment.types";

export const usePaymentAccounts = () => {
  const { i18n } = useTranslation();

  const {
    data: userPaymentAccount,
    isLoading: isUserPaymentAccountLoading,
    isError: isUserPaymentAccountHasError,
  } = useQuery({
    queryKey: ["Payment", "PaymentAccounts", i18n.language],
    queryFn: async () => await getPaymentAccounts(),
  });

  return { userPaymentAccount, isUserPaymentAccountLoading, isUserPaymentAccountHasError };
};

export const useStripeAccountId = () => {
  const { i18n } = useTranslation();

  const {
    data: stripeAccountId,
    isLoading: isStripeAccountIdLoading,
    isError: isStripeAccountIdHasError,
  } = useQuery({
    queryKey: ["Payment", "StripeAccountId", i18n.language],
    queryFn: async () => await createStripeAccountId(),
  });

  return { stripeAccountId, isStripeAccountIdLoading, isStripeAccountIdHasError };
};

export const usePaymentLinks = ({
  page,
  searchValue,
  dateRange,
  status,
}: {
  page: number;
  searchValue?: string;
  dateRange: [Date | null, Date | null];
  status: string;
}) => {
  const { i18n } = useTranslation();
  const [minDate, maxDate] = dateRange;
  const areBothNull = !minDate && !maxDate;
  const areBothDates = Boolean(minDate) && Boolean(maxDate);
  const shouldEnable = areBothNull || areBothDates;
  const {
    data: userPaymentLinks,
    isLoading: isUserPaymentLinksLoading,
    isError: isUserPaymentLinksHasError,
  } = useQuery({
    queryKey: ["Payment", "PaymentLinks", i18n.language, page, searchValue, dateRange, status],
    queryFn: async () => await getPaymentLinks({ page, searchValue, minDate, maxDate, status }),
    enabled: shouldEnable,
    placeholderData: keepPreviousData,
  });

  return { userPaymentLinks, isUserPaymentLinksLoading, isUserPaymentLinksHasError };
};

export const usePaymentHistory = ({
  page,
  search,
  status,
}: {
  page: number;
  search?: string;
  status?: TableActiveState;
}) => {
  const { i18n } = useTranslation();

  const {
    data: userPaymentHistory,
    isLoading: isUserPaymentHistoryLoading,
    isError: isUserPaymentHistoryHasError,
  } = useQuery({
    queryKey: ["Payment", "PaymentHistory", i18n.language, page, search, status],
    queryFn: async () => await getPaymentsHistory({ page, search, status }),
  });

  return { userPaymentHistory, isUserPaymentHistoryLoading, isUserPaymentHistoryHasError };
};

export const usePaymentOverview = () => {
  const { i18n } = useTranslation();

  const {
    data: userPaymentOverview,
    isLoading: isUserPaymentOverviewLoading,
    isError: isUserPaymentOverviewHasError,
  } = useQuery({
    queryKey: ["Payment", "PaymentOverview", i18n.language],
    queryFn: async () => await getPaymentsOverview(),
  });

  return { userPaymentOverview, isUserPaymentOverviewLoading, isUserPaymentOverviewHasError };
};

export const useTotalPaymentsPerDay = ({ dateRange }: { dateRange: [Date | null, Date | null] }) => {
  const { i18n } = useTranslation();

  const {
    data: userTotalPaymentsPerDay,
    isLoading: isUserTotalPaymentsPerDayLoading,
    isError: isUserTotalPaymentsPerDayHasError,
  } = useQuery({
    queryKey: ["Payment", "TotalPayments", i18n.language, dateRange],
    queryFn: async () => await getTotalPaymentsPerDay({ dateRange }),
  });

  return { userTotalPaymentsPerDay, isUserTotalPaymentsPerDayLoading, isUserTotalPaymentsPerDayHasError };
};

export const useInvoiceDetails = (invoiceId: number | null) => {
  const { i18n } = useTranslation();

  const {
    data: invoiceDetails,
    isLoading: isInvoiceDetailsLoading,
    isError: isInvoiceDetailsHasError,
  } = useQuery({
    queryKey: ["Payment", "InvoiceDetails", i18n.language, invoiceId],
    queryFn: async () => await getInvoiceDetails(invoiceId),
    enabled: !!invoiceId,
  });

  return { invoiceDetails, isInvoiceDetailsLoading, isInvoiceDetailsHasError };
};

export const useMinimumServicePrices = () => {
  const { i18n } = useTranslation();

  const {
    data: minimumServicePrices,
    isLoading: isMinimumServicePricesLoading,
    isError: isMinimumServicePricesHasError,
  } = useQuery({
    queryKey: ["Payment", "MinimumServicePrices", i18n.language],
    queryFn: async () => await getMinimumServicePrices(),
  });

  return { minimumServicePrices, isMinimumServicePricesLoading, isMinimumServicePricesHasError };
};

export const useWalletSummary = () => {
  const { i18n } = useTranslation();

  const {
    data: walletSummary,
    isLoading: isWalletSummaryLoading,
    isError: isWalletSummaryHasError,
  } = useQuery({
    queryKey: ["Payment", "WalletSummary", i18n.language],
    queryFn: async () => await getWalletSummary(),
  });

  return { walletSummary, isWalletSummaryLoading, isWalletSummaryHasError };
};
